import clsx from 'clsx';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { getSvgIconPath } from '../utils/svg';
import type { SvgIconProps } from './SvgIcon.types';

/**
 * Renders an icon that has an SVG icon path (see {@link isSvgIcon() })
 *
 * @param onClick - function to call when clicking the icon (takes no parameters)
 * @param id - id that can be placed on the SvgIcon component
 * @param testId - id that will be used in the data-testid attribute on the icon
 * @param customId - id that will be used in the data-customid attribute on the icon. Can be used to identify the icon
 *   as the click event target in an event handler
 * @param icon - the SVG icon
 * @param color - used to add a custom color to the icon (required if type="color")
 * @param extraClassNames - extra class names to apply
 * @param [viewBox='0 0 19 19'] - optional SVG view box
 * @param type - default will use dark/light text colors otherwise will use the theme color
 * @param tooltipProps - props to pass to the tooltip
 */
const SvgIcon = ({
  onClick,
  icon,
  color,
  type = 'default',
  extraClassNames,
  viewBox = '0 0 19 19',
  id,
  testId,
  customId,
  ...tooltipProps
}: SvgIconProps) => {
  const tooltipData = getQTipData(tooltipProps);
  const appliedType = type === 'default' ? 'currentColor' : 'var(--theme-icon)';

  return (
    <div
      onClick={onClick}
      {...tooltipData}
      className={clsx(
        // eslint-disable-next-line better-tailwindcss/no-unknown-classes
        'svgContainer',
        'tw:outline-none tw:focus:outline-none tw:focus-visible:outline-none',
        { 'tw:cursor-pointer': onClick },
        extraClassNames,
      )}>
      <svg
        id={id}
        data-customid={customId}
        xmlns="http://www.w3.org/2000/svg"
        className="tw:text-sq-theme-icon tw:dark:text-sq-white"
        viewBox={viewBox}
        fill={color ? color : appliedType}
        data-testid={testId}>
        <path d={getSvgIconPath(icon)} />
      </svg>
    </div>
  );
};
export default SvgIcon;
